import { getCurrencySymbol as getSymbol, getCurrencyLocale } from '@/constants/currencies';
import { getCurrentLanguage } from '@/i18n';
import type { SystemSettings } from '@/store/api/settingsApi';

// Currency formatting with system settings support
interface CurrencyFormatOptions {
  includeSymbol?: boolean;
}

export const formatCurrency = (
  amount: number,
  currency?: string,
  locale?: string,
  options: CurrencyFormatOptions = {}
): string => {
  // Ensure amount is a valid number
  const validAmount = isNaN(amount) ? 0 : amount;

  // Get settings from parameters or use defaults
  const systemCurrency = currency || 'IDR';
  // If no locale provided, try to get it from currency, fallback to id-ID
  const systemLocale = locale || getCurrencyLocale(systemCurrency) || 'id-ID';
  const includeSymbol = options.includeSymbol ?? true;

  try {
    // Use appropriate locale for formatting
    const formatted = new Intl.NumberFormat(systemLocale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(validAmount);

    // Return with appropriate currency symbol
    if (!includeSymbol) {
      return formatted;
    }
    const currencySymbol = getCurrencySymbol(systemCurrency);
    return `${currencySymbol}${formatted}`;
  } catch (error) {
    // Fallback to basic formatting if Intl fails
    const fallback = validAmount.toLocaleString();
    if (!includeSymbol) {
      return fallback;
    }
    const currencySymbol = getCurrencySymbol(systemCurrency);
    return `${currencySymbol}${fallback}`;
  }
};

// Helper function to get currency symbol
const getCurrencySymbol = getSymbol;

export type CurrencyFormatSource = Pick<SystemSettings, 'currency' | 'language'> | {
  currency?: string;
  language?: string;
};

const normalizeLanguageToLocale = (language?: string): string | undefined => {
  if (!language) return undefined;
  if (language.includes('-')) {
    return language;
  }
  switch (language) {
    case 'id':
      return 'id-ID';
    case 'en':
      return 'en-US';
    default:
      return language;
  }
};

export const resolveLocaleFromSettings = (settings?: CurrencyFormatSource): string | undefined => {
  if (!settings) return undefined;
  const byLanguage = normalizeLanguageToLocale(settings.language);
  if (byLanguage) return byLanguage;
  if (settings.currency) {
    return getCurrencyLocale(settings.currency) || undefined;
  }
  return undefined;
};

export const formatCurrencyWithSettings = (
  amount: number,
  settings?: CurrencyFormatSource,
  options: CurrencyFormatOptions = {}
): string => {
  const currencyCode = settings?.currency;
  const locale = resolveLocaleFromSettings(settings);
  return formatCurrency(amount, currencyCode, locale, options);
};

export const sanitizeMoneyInput = (value: string, allowNegative: boolean = false): string => {
  if (!value) return '';
  const pattern = allowNegative ? /[^0-9-]/g : /[^0-9]/g;
  return value.replace(pattern, '');
};

export const formatMoneyInputDisplay = (
  value: string,
  settings?: CurrencyFormatSource
): string => {
  if (!value) return '';
  if (value === '-') return '-';
  const locale = resolveLocaleFromSettings(settings) || 'id-ID';
  try {
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString(locale);
  } catch (error) {
    return value;
  }
};

// Number formatting with locale awareness
export const formatNumber = (
  value: number,
  locale?: string,
  options?: Intl.NumberFormatOptions
): string => {
  const currentLanguage = getCurrentLanguage();
  const formatLocale = locale || (currentLanguage === 'id' ? 'id-ID' : 'en-US');

  return new Intl.NumberFormat(formatLocale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(value);
};

// Date formatting with locale awareness
export const formatDate = (
  date: string | Date,
  format?: 'date' | 'datetime' | 'time' | Intl.DateTimeFormatOptions,
  locale?: string
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const currentLanguage = getCurrentLanguage();
  const formatLocale = locale || (currentLanguage === 'id' ? 'id-ID' : 'en-US');

  // Handle string format types
  if (typeof format === 'string') {
    switch (format) {
      case 'datetime':
        return formatDateTime(dateObj, undefined, formatLocale);
      case 'time':
        return formatTime(dateObj, undefined, formatLocale);
      case 'date':
      default:
        return new Intl.DateTimeFormat(formatLocale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }).format(dateObj);
    }
  }

  // Handle options object
  return new Intl.DateTimeFormat(formatLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...format,
  }).format(dateObj);
};

export const formatDateTime = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const currentLanguage = getCurrentLanguage();
  const formatLocale = locale || (currentLanguage === 'id' ? 'id-ID' : 'en-US');

  return new Intl.DateTimeFormat(formatLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }).format(dateObj);
};

export const formatTime = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const currentLanguage = getCurrentLanguage();
  const formatLocale = locale || (currentLanguage === 'id' ? 'id-ID' : 'en-US');

  return new Intl.DateTimeFormat(formatLocale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options,
  }).format(dateObj);
};

// Resolve a UTC-midnight-serialized date-only value (e.g. a Postgres @db.Date
// column) to a Date at local midnight of the same calendar day, so it can be
// safely formatted or compared without shifting to an adjacent day in
// negative-UTC-offset browsers
export const toLocalDateFromUtcDateOnly = (value: string | Date): Date => {
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// Relative time formatting with i18n support
export const formatRelativeTime = (date: string | Date, t?: (key: string, options?: any) => string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return t ? t('common:time.just_now') : 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    if (t) {
      return minutes === 1
        ? t('common:time.minutes_ago', { count: minutes })
        : t('common:time.minutes_ago_plural', { count: minutes });
    }
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    if (t) {
      return hours === 1
        ? t('common:time.hours_ago', { count: hours })
        : t('common:time.hours_ago_plural', { count: hours });
    }
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    if (t) {
      return days === 1
        ? t('common:time.days_ago', { count: days })
        : t('common:time.days_ago_plural', { count: days });
    }
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateObj);
  }
};

// Phone number formatting
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // Format Indonesian phone numbers
  if (digits.startsWith('62')) {
    // International format: +62 xxx-xxxx-xxxx
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(5, 9)}-${digits.slice(9)}`;
  } else if (digits.startsWith('0')) {
    // Local format: 0xxx-xxxx-xxxx
    return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
  }

  return phone; // Return original if no pattern matches
};

// Percentage formatting
export const formatPercentage = (
  value: number,
  decimals: number = 1
): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

// File size formatting
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Alias for backward compatibility
export const formatBytes = formatFileSize;

// Text truncation
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

// Capitalize first letter
export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Title case
export const toTitleCase = (text: string): string => {
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Kebab case to title case
export const kebabToTitle = (text: string): string => {
  return text
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Generate initials from name
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Generate random string
export const generateRandomString = (length: number = 8): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Generate transaction code
export const generateTransactionCode = (): string => {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const timeStr = date.toTimeString().slice(0, 8).replace(/:/g, '');
  const randomStr = generateRandomString(4);
  return `TXN-${dateStr}-${timeStr}-${randomStr}`;
};

// Format transaction status
export const formatTransactionStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    PENDING: 'Pending',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    RETURNED: 'Returned',
  };
  return statusMap[status] || status;
};

// Format payment method
export const formatPaymentMethod = (method: string): string => {
  const methodMap: Record<string, string> = {
    CASH: 'Cash',
    CARD: 'Card',
    TRANSFER: 'Bank Transfer',
    QRIS: 'QRIS',
    DEBIT_CARD: 'Debit Card',
  };
  return methodMap[method] || method;
};

// Format quantity with decimal support
export const formatQuantity = (quantity: number | string, maxDecimals: number = 2): string => {
  const numQuantity = typeof quantity === 'string' ? parseFloat(quantity) : quantity;

  if (isNaN(numQuantity)) return '0';

  // If it's a whole number, don't show decimals
  if (numQuantity % 1 === 0) {
    return numQuantity.toString();
  }

  // Otherwise, format with up to maxDecimals decimal places, removing trailing zeros
  return parseFloat(numQuantity.toFixed(maxDecimals)).toString();
};

// Format date to YYYY-MM-DD in local timezone (no UTC conversion)
export const formatDateToLocalString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
